import { useState } from "react";
import { homeFaqs } from "../../mocks/faq";
import { useReveal } from "../../hooks/useReveal";

interface FAQ {
  q: string;
  a: string;
}

interface FAQSectionProps {
  faqs?: FAQ[];
  title?: string;
  subtitle?: string;
}

export default function FAQSection({
  faqs = homeFaqs,
  title = "Common Questions",
  subtitle = "Everything you need to know before booking",
}: FAQSectionProps) {
  const [open, setOpen] = useState<number | null>(0);
  const ref = useReveal<HTMLElement>();

  return (
    <section ref={ref} className="bg-pk-ivory py-28 overflow-hidden">
      <div className="max-w-screen-xl mx-auto px-8">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-16">
          {/* Left sticky */}
          <div className="md:col-span-2">
            <div className="reveal md:sticky md:top-28">
              <span className="font-sans text-xs tracking-widest3 uppercase text-pk-gold">FAQ</span>
              <h2 className="font-display text-5xl md:text-6xl font-medium text-pk-charcoal mt-4 leading-tight">
                {title}
              </h2>
              <p className="font-sans text-sm text-pk-muted mt-6 leading-7">{subtitle}</p>

              {/* Decorative separator */}
              <div className="mt-10 w-12 h-px bg-pk-gold" />

              {/* Call CTA */}
              <a
                href="tel:2108154698"
                className="mt-8 inline-flex items-center gap-3 font-sans text-sm text-pk-charcoal hover:text-pk-gold transition-colors duration-300 cursor-pointer group"
              >
                <span className="w-10 h-10 flex items-center justify-center bg-pk-gold rounded-full flex-shrink-0">
                  <i className="ri-phone-line text-white text-sm" />
                </span>
                <span>Still have questions? Call us</span>
              </a>

              {/* Additional mini CTA */}
              <div className="mt-6 bg-pk-cream rounded-sm p-6 border border-pk-light">
                <p className="font-display text-xl italic text-pk-charcoal leading-snug">
                  &ldquo;She was very patient with our baby. Highly recommend!&rdquo;
                </p>
                <p className="font-sans text-xs text-pk-muted mt-3">— M. Gutierrez, Newborn Client</p>
              </div>
            </div>
          </div>

          {/* Right: Accordion */}
          <div className="md:col-span-3 space-y-2">
            {faqs.map((faq, i) => (
              <div
                key={i}
                className="reveal border border-pk-light rounded-sm overflow-hidden"
                style={{ transitionDelay: `${i * 80}ms` }}
              >
                <button
                  onClick={() => setOpen(open === i ? null : i)}
                  className="w-full flex items-start justify-between px-7 py-5 text-left cursor-pointer group gap-4"
                >
                  <h3 className="font-sans text-sm font-medium text-pk-charcoal group-hover:text-pk-gold transition-colors duration-300 leading-6">
                    {faq.q}
                  </h3>
                  <div className="w-7 h-7 flex items-center justify-center flex-shrink-0 bg-pk-light rounded-full mt-0.5 group-hover:bg-pk-gold group-hover:text-white transition-colors duration-300">
                    <i className={`text-pk-charcoal group-hover:text-white transition-all duration-300 text-sm ${open === i ? "ri-subtract-line" : "ri-add-line"}`} />
                  </div>
                </button>
                <div
                  className={`overflow-hidden transition-all duration-500 ${open === i ? "max-h-64" : "max-h-0"}`}
                >
                  <div className="px-7 pb-6 border-t border-pk-light">
                    <p className="font-sans text-sm text-pk-muted leading-7 pt-5">{faq.a}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
